import { MODES_DE_LA_CARTE, type ModeDeLaCarte } from '@nextilt/commun';
import {
  CHERCHE_DANS_LE_PROJET,
  HIERARCHISE,
  infosEnMots,
  LIE_DES_BULLES,
  MONTRE_LES_LIENS_CACHES,
  type MOTS_DES_INFOS,
  OUVRE_L_AVANCEMENT,
  OUVRE_LA_FRISE,
  OUVRE_LE_REPERTOIRE,
  POSE_UN_POST_IT,
  POSE_UNE_LISTE,
  REGROUPE_LES_BULLES,
  OUVRE_LE_PANNEAU_D_ANALYSE,
  OUVRE_LE_PANNEAU_MODE,
  REMET_TOUT,
  TOUCHE_CTRL,
  TOUCHE_ECHAP,
} from '../partage/vocabulaire';

/*
 * **Les touches de la carte, écrites une fois** (chantier modes-de-la-carte, modification 01,
 * décisions d'Igor du 2026-10-21) : ce que fait chaque touche dans chaque mode d'un projet. Le
 * panneau « Mode » les lit pour les expliquer ; le gestionnaire de touches de la Carte et de la
 * Hiérarchie (modification 02) les lit pour agir. Jamais une table par vue.
 */

/** Ce qu'une touche d'info montre à la place du titre d'une bulle. */
export type InfoDUneBulle = keyof typeof MOTS_DES_INFOS;

/** Les marges qu'ouvrent M, R, F (modification 03). */
export type Marge = 'avancement' | 'repertoire' | 'frise';

/** Ce que fait une touche. */
export type Geste =
  | { sorte: 'marge'; marge: Marge }
  | { sorte: 'hierarchiser' }
  /** J : lance « Lier », comme H lance Hiérarchiser (chantier liens-entre-bulles, ticket 04). */
  | { sorte: 'lier' }
  /** A : montre les liens cachés de la carte, puis les recache (chantier liens-entre-bulles, 03). */
  | { sorte: 'liens_caches' }
  | { sorte: 'chercher' }
  | { sorte: 'remettre' }
  | { sorte: 'papier'; papier: 'post_it' | 'liste' }
  | { sorte: 'infos'; infos: readonly InfoDUneBulle[] }
  | { sorte: 'regrouper' }
  /** M en Analyse : le panneau d'analyse, à la place de « Comment j'avance » (`theme-analyse`). */
  | { sorte: 'analyse' }
  /** Ctrl seul : ouvre ce panneau ; tenu par le panneau lui-même (`HabitDuProjet`), pas par la carte. */
  | { sorte: 'mode' };

/** Une touche : `cle` est `event.key` en minuscules, `touche` ce que l'écran écrit. */
export interface Raccourci {
  cle: string;
  touche: string;
  geste: Geste;
}

const raccourci = (cle: string, geste: Geste): Raccourci => ({
  cle,
  touche: cle === 'escape' ? TOUCHE_ECHAP : cle === 'control' ? TOUCHE_CTRL : cle.toUpperCase(),
  geste,
});

/**
 * Partout : M, R, F ouvrent les marges, H hiérarchise, J lie (chantier liens-entre-bulles, ticket
 * 04), A montre les liens cachés (ticket 03), / cherche, Échap remet tout, Ctrl seul ouvre le
 * panneau Mode. Analyse ne garde ni H, ni J, ni A : son jeu de touches est une décision d'Igor
 * (theme-analyse).
 */
export const TOUCHES_DE_PARTOUT: readonly Raccourci[] = [
  raccourci('m', { sorte: 'marge', marge: 'avancement' }),
  raccourci('r', { sorte: 'marge', marge: 'repertoire' }),
  raccourci('f', { sorte: 'marge', marge: 'frise' }),
  raccourci('h', { sorte: 'hierarchiser' }),
  raccourci('j', { sorte: 'lier' }),
  raccourci('a', { sorte: 'liens_caches' }),
  raccourci('/', { sorte: 'chercher' }),
  raccourci('escape', { sorte: 'remettre' }),
  raccourci('control', { sorte: 'mode' }),
];

/**
 * Ce que chaque mode ajoute, et ce qu'il garde de celles de partout : `'toutes'`, ou leurs clés.
 * Analyse (chantier theme-analyse, ticket 01, décision d'Igor du 2026-10-21, « c'est de l'analyse
 * pas de l'execution ou de la creation ») : G regroupe, M ouvre le panneau d'analyse, et de
 * partout seulement /, Échap, Ctrl ; ni C, D, T, P, L, R, F, H.
 */
export const TOUCHES_DES_MODES: Readonly<
  Record<ModeDeLaCarte, { touches: readonly Raccourci[]; dePartout: 'toutes' | readonly string[] }>
> = {
  'de-base': { touches: [], dePartout: 'toutes' },
  reflexion: {
    touches: [
      raccourci('p', { sorte: 'papier', papier: 'post_it' }),
      raccourci('l', { sorte: 'papier', papier: 'liste' }),
      raccourci('c', { sorte: 'infos', infos: ['contact'] }),
      raccourci('d', { sorte: 'infos', infos: ['date'] }),
      raccourci('t', { sorte: 'infos', infos: ['duree'] }),
    ],
    dePartout: 'toutes',
  },
  execution: {
    touches: [
      raccourci('c', { sorte: 'infos', infos: ['contactEtNumero', 'lieu'] }),
      raccourci('d', { sorte: 'infos', infos: ['date', 'lieu'] }),
      raccourci('t', { sorte: 'infos', infos: ['duree', 'echeance'] }),
    ],
    dePartout: 'toutes',
  },
  analyse: {
    touches: [raccourci('g', { sorte: 'regrouper' }), raccourci('m', { sorte: 'analyse' })],
    dePartout: ['/', 'escape', 'control'],
  },
};

/** Les modes, dans l'ordre du panneau. */
export const MODES = MODES_DE_LA_CARTE;

/** Toutes les touches d'un mode : les siennes, puis celles de partout qu'il garde. */
export function touchesDuMode(mode: ModeDeLaCarte): readonly Raccourci[] {
  const { touches, dePartout } = TOUCHES_DES_MODES[mode];
  const siennes = new Set(touches.map((r) => r.cle));
  const gardees = TOUCHES_DE_PARTOUT.filter(
    (r) => !siennes.has(r.cle) && (dePartout === 'toutes' || dePartout.includes(r.cle))
  );
  return [...touches, ...gardees];
}

/** Les modes qui gardent toutes les touches de partout : le panneau « Mode » les nomme. */
export function modesQuiGardentTout(): ModeDeLaCarte[] {
  return MODES.filter((m) => TOUCHES_DES_MODES[m].dePartout === 'toutes');
}

/** Le geste d'une touche dans un mode, ou `null` : rien. */
export function gesteDeLaTouche(mode: ModeDeLaCarte, cle: string): Geste | null {
  return touchesDuMode(mode).find((r) => r.cle === cle.toLowerCase())?.geste ?? null;
}

/** Ce que fait un geste, en mots : le panneau « Mode » et la ligne du bas de la carte. */
export function gesteEnMots(geste: Geste): string {
  switch (geste.sorte) {
    case 'marge':
      return { avancement: OUVRE_L_AVANCEMENT, repertoire: OUVRE_LE_REPERTOIRE, frise: OUVRE_LA_FRISE }[geste.marge];
    case 'hierarchiser':
      return HIERARCHISE;
    case 'lier':
      return LIE_DES_BULLES;
    case 'liens_caches':
      return MONTRE_LES_LIENS_CACHES;
    case 'chercher':
      return CHERCHE_DANS_LE_PROJET;
    case 'remettre':
      return REMET_TOUT;
    case 'papier':
      return geste.papier === 'post_it' ? POSE_UN_POST_IT : POSE_UNE_LISTE;
    case 'infos':
      return infosEnMots(geste.infos);
    case 'regrouper':
      return REGROUPE_LES_BULLES;
    case 'analyse':
      return OUVRE_LE_PANNEAU_D_ANALYSE;
    case 'mode':
      return OUVRE_LE_PANNEAU_MODE;
  }
}
